<script context="module">
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { useForm, router } from "@inertiajs/svelte";
  import { toast } from "svelte-sonner";
  import { 
    Save, ArrowLeft, Code, Blocks, 
    Trash2, Loader2, ChevronRight, AlertCircle
  } from "lucide-svelte";

  // 1. 接收 Props (Rails 8.1 传回的数据)
  let { page: initialPage, site_config, translations = {} } = $props();

  console.log(`initialPage: ${JSON.stringify(initialPage)},site_config:${JSON.stringify(site_config)},translations:${JSON.stringify(translations)}`)
  // 2. 状态控制
  let currentTab = $state<"basic" | "content">("basic");

  // 3. 核心修复：Svelte 5 派生状态，确保循环不报错
  const supportedLocales = $derived(site_config?.supported_locales || [initialPage?.locale || 'zh']);

  // SEO 默认模板生成器
  const generateDefaultSeo = (title: string) => JSON.stringify({
    title: title || "新页面",
    description: `关于 ${title || '新页面'} 的详细介绍...`,
    keywords: `${title}, ${site_config?.name || '官网'}`
  }, null, 2);

  // 4. 初始化 Inertia Form
  const form = useForm({
    id: initialPage?.id,
    title: initialPage?.title || "",
    slug: initialPage?.slug || "",
    locale: initialPage?.locale || site_config?.default_locale || "zh",
    mode: initialPage?.mode || "components",
    // 将 JSON 对象转为字符串供编辑器使用
    content_blocks_raw: JSON.stringify(initialPage?.content_blocks || { blocks: [] }, null, 2),
    seo_metadata_raw: (initialPage?.seo_metadata && Object.keys(initialPage.seo_metadata).length > 0)
      ? JSON.stringify(initialPage.seo_metadata, null, 2)
      : generateDefaultSeo(initialPage?.title),
    raw_html: initialPage?.raw_html || ""
  });

  
    if (initialPage && !form.isDirty) {
      form.title = initialPage.title || "";
      form.slug = initialPage.slug || "";
      form.locale = initialPage.locale || "zh";
      form.mode = initialPage.mode || "components";
    }
  

  // 6. 语言切换逻辑 (处理 Translations)
  function switchLocale(newLocale: string) {
    if (form.locale === newLocale) return;
    if (form.isDirty && !confirm(`当前 [${form.locale.toUpperCase()}] 版本有修改，切换将丢失未保存内容，确定吗？`)) return;

    // 如果选中的是主语言，用 initialPage；否则从 translations 里找
    const targetData = newLocale === initialPage.locale ? initialPage : (translations[newLocale] || {});

    form.locale = newLocale;
    form.title = targetData.title || "";
    form.content_blocks_raw = JSON.stringify(targetData.content_blocks || { blocks: [] }, null, 2);
    form.raw_html = targetData.raw_html || "";
    
    if (targetData.seo_metadata && Object.keys(targetData.seo_metadata).length > 0) {
      form.seo_metadata_raw = JSON.stringify(targetData.seo_metadata, null, 2);
    } else {
      form.seo_metadata_raw = generateDefaultSeo(targetData.title || form.title);
    }
    
    if (typeof form.defaults === 'function') form.defaults();
    toast.info(`已载入 ${newLocale.toUpperCase()} 语言数据`);
  }

  // 7. 实时校验 JSON 格式
  let isJsonValid = $derived.by(() => {
    try {
      if (form.mode === 'components' && form.content_blocks_raw?.trim()) JSON.parse(form.content_blocks_raw);
      if (form.seo_metadata_raw?.trim()) JSON.parse(form.seo_metadata_raw);
      return true;
    } catch(e) { 
        console.log(e)
        return false; 
    }
  });

  function savePage() {
    // 1. JSON 校验
    try {
      if (form.mode === 'components') {
        JSON.parse(form.content_blocks_raw || "{}");
      }
      JSON.parse(form.seo_metadata_raw || "{}");
    } catch (e) {
      return toast.error("JSON 格式不合法");
    }

    // 2. 区分【新建】与【更新】
    const isNew = !initialPage?.id;
    const method = isNew ? "post" : "put";
    const url = isNew ? "/pages" : `/pages/${initialPage.id}`;

    // 3. 提交数据
    router[method](url, {
      page: {
        title: form.title,
        slug: form.slug,
        locale: form.locale,
        mode: form.mode,
        content_blocks_raw: form.content_blocks_raw,
        seo_metadata_raw: form.seo_metadata_raw,
        raw_html: form.raw_html
      }
    }, {
      onStart: () => form.processing = true,
      onFinish: () => form.processing = false,
      onSuccess: () => {
        toast.success(isNew ? "创建成功！" : "保存成功！");
        if (typeof form.defaults === 'function') form.defaults();
      }
    });
  }

  function deletePage() {
    if (!confirm("确定要永久删除这个页面吗？此操作不可撤销。")) return;
    // 这里也统一改为 /pages/${initialPage.id}
    router.delete(`/pages/${initialPage.id}`, {
      onSuccess: () => toast.success("页面已删除")
    });
  }
</script>

<div class="p-8 max-w-6xl mx-auto min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-all">
  <div class="flex items-center justify-between mb-8">
    <button onclick={() => router.visit("/pages")} class="flex items-center gap-2 text-zinc-400 hover:text-zinc-900 transition-colors">
      <ArrowLeft class="size-5" />
      <span class="text-sm font-medium">返回列表</span>
    </button>

    <div class="flex items-center gap-4">
      <button 
        onclick={deletePage}
        class="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
        title="删除页面"
      >
        <Trash2 class="size-5" />
      </button>
      
      {#if form.isDirty}
        <span class="text-[10px] font-bold text-amber-500 uppercase px-3 py-1 bg-amber-50 dark:bg-amber-500/10 rounded-full border border-amber-200">未保存修改</span>
      {/if}

      <button 
        onclick={savePage} 
        disabled={form.processing}
        class="flex items-center gap-2 bg-zinc-900 dark:bg-blue-600 text-white px-8 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-30 font-bold text-sm shadow-xl transition-all"
      >
        {#if form.processing}<Loader2 class="size-4 animate-spin" />{:else}<Save class="size-4" />{/if}
        保存页面
      </button>
    </div>
  </div>

  <div class="mb-10">
    <h1 class="text-3xl font-black tracking-tighter mb-2">编辑内容资产</h1>
    <div class="flex items-center gap-2 text-zinc-400 text-[10px] font-mono uppercase">
      <span>{site_config?.name || 'Guangyan'}</span>
      <ChevronRight class="size-3" />
      <span>{form.slug || 'index'}</span>
    </div>
  </div>

  <div class="flex gap-8 border-b border-zinc-100 dark:border-zinc-800 mb-8">
    {#each ['basic', 'content'] as tab}
      <button 
        onclick={() => currentTab = tab as any} 
        class="pb-4 text-sm font-bold transition-all relative {currentTab === tab ? 'text-zinc-950 dark:text-white' : 'text-zinc-400'}"
      >
        {tab === 'basic' ? '基本配置' : '内容编辑'}
        {#if currentTab === tab}
          <div class="absolute bottom-0 left-0 w-full h-0.5 bg-zinc-950 dark:bg-blue-500"></div>
        {/if}
      </button>
    {/each}
  </div>

  {#if currentTab === "basic"}
    <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
      <div class="space-y-6">
        <div>
          <label class="block text-[10px] font-black text-zinc-400 uppercase mb-2 px-1">页面标题</label>
          <input type="text" bind:value={form.title} class="w-full px-5 py-4 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl outline-none focus:ring-2 focus:ring-zinc-100" />
        </div>

        <div>
          <label class="block text-[10px] font-black text-zinc-400 uppercase mb-2 px-1">路由路径</label>
          <div class="flex items-center bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-5">
            <span class="text-zinc-400 font-mono">/</span>
            <input type="text" bind:value={form.slug} class="flex-1 bg-transparent py-4 text-sm font-mono outline-none" />
          </div>
        </div>
      </div>

      <div class="space-y-6">
        <div>
          <label class="block text-[10px] font-black text-zinc-400 uppercase mb-2 px-1">语言版本控制</label>
          <div class="flex gap-2 flex-wrap bg-zinc-50 dark:bg-zinc-900 p-2 rounded-2xl border border-zinc-200 dark:border-zinc-800">
            {#each supportedLocales as locale}
              <button 
                onclick={() => switchLocale(locale)}
                class="px-5 py-2.5 rounded-xl text-xs font-bold transition-all {form.locale === locale ? 'bg-zinc-950 text-white shadow-lg' : 'text-zinc-400 hover:bg-zinc-200'}"
              >
                {locale.toUpperCase()}
              </button>
            {/each}
          </div>
        </div>

        <div>
          <label class="block text-[10px] font-black text-zinc-400 uppercase mb-2 px-1">编辑器模式</label>
          <div class="grid grid-cols-2 gap-3">
            <button 
              onclick={() => form.mode = "components"}
              class="py-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all {form.mode === 'components' ? 'border-zinc-950 bg-zinc-950 text-white shadow-xl' : 'border-zinc-100 text-zinc-400'}"
            >
              <Blocks class="size-5" />
              <span class="text-[10px] font-bold">COMPONENTS</span>
            </button>
            <button 
              onclick={() => form.mode = "code"}
              class="py-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all {form.mode === 'code' ? 'border-amber-500 bg-amber-500 text-white shadow-xl' : 'border-zinc-100 text-zinc-400'}"
            >
              <Code class="size-5" />
              <span class="text-[10px] font-bold">RAW CODE</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="space-y-8 animate-in fade-in slide-in-from-bottom-2">
      <div class="bg-zinc-950 rounded-3xl overflow-hidden border border-zinc-800 shadow-2xl">
        <div class="px-6 py-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/50 text-[10px] font-mono text-zinc-500">
          <div class="flex items-center gap-2">
            <div class="size-2 rounded-full {isJsonValid ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}"></div>
            <span>{form.mode.toUpperCase()} EDITOR</span>
          </div>
          <span>LOCALE: {form.locale.toUpperCase()}</span>
        </div>
        {#if form.mode === "components"}
          <textarea bind:value={form.content_blocks_raw} spellcheck="false" class="w-full h-[500px] p-8 font-mono text-sm bg-transparent text-blue-400/90 outline-none resize-none leading-relaxed"></textarea>
        {:else}
          <textarea bind:value={form.raw_html} spellcheck="false" class="w-full h-[500px] p-8 font-mono text-sm bg-transparent text-amber-200/90 outline-none resize-none leading-relaxed"></textarea>
        {/if}
      </div>
      
      <div class="bg-zinc-50 dark:bg-zinc-900/50 p-6 rounded-3xl border border-zinc-100 dark:border-zinc-800">
        <label class="block text-[10px] font-black text-zinc-400 uppercase mb-3 px-1">SEO Metadata (JSON)</label>
        <textarea bind:value={form.seo_metadata_raw} class="w-full h-32 p-5 font-mono text-xs bg-white dark:bg-black border border-zinc-200 rounded-2xl outline-none text-zinc-500 focus:ring-2 focus:ring-zinc-100 transition-all"></textarea>
      </div>
    </div>
  {/if}
</div>